import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Globe, Lock, Users, Trash2, Loader2, PlayCircle } from 'lucide-react';
import { usePostActions, usePost } from '../hooks/usePosts';
import { toast } from 'sonner';

interface EditPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  currentCaption?: string;
  currentPrivacy: 'public' | 'private' | 'followers_only';
}

const EditPostModal = ({ 
  isOpen, 
  onClose, 
  postId, 
  currentCaption = '', 
  currentPrivacy 
}: EditPostModalProps) => {
  const { t } = useTranslation();
  const [caption, setCaption] = useState(currentCaption);
  const [privacy, setPrivacy] = useState<'public' | 'private' | 'followers_only'>(currentPrivacy);
  const { updatePost, isUpdating, deletePostMedia, isDeletingMedia } = usePostActions();
  const { data: post, refetch: refetchPost } = usePost(isOpen ? postId : undefined);
  const [mediaToDelete, setMediaToDelete] = useState<{ id: string; url: string } | null>(null);
  const media = (post as any)?.media ?? [];

  const handleConfirmDeleteMedia = () => {
    if (!mediaToDelete) return;
    deletePostMedia(mediaToDelete.id, {
      onSuccess: () => {
        toast.success(t('modals.editPost.mediaDeletedSuccess', 'Media deleted'));
        setMediaToDelete(null);
        refetchPost();
      },
      onError: (error) => {
        toast.error(error?.message || t('common.unknownError'));
      },
    });
  };

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setCaption(currentCaption);
      setPrivacy(currentPrivacy);
    }
  }, [isOpen, currentCaption, currentPrivacy]);

  const handleSave = () => {
    updatePost(postId, { caption, privacy_setting: privacy }, {
      onSuccess: () => {
        toast.success(t('modals.editPost.success'));
        onClose();
      },
      onError: (error) => {
        toast.error(t('modals.editPost.error', { error: error?.message || t('common.unknownError') }));
      }
    });
  };

  const privacyOptions = [
    { 
      value: 'public', 
      label: t('modals.editPost.privacyPublic'), 
      description: t('modals.editPost.privacyPublicDesc'),
      icon: Globe 
    },
    { 
      value: 'followers_only', 
      label: t('modals.editPost.privacyFollowers'), 
      description: t('modals.editPost.privacyFollowersDesc'),
      icon: Users 
    },
    { 
      value: 'private', 
      label: t('modals.editPost.privacyPrivate'), 
      description: t('modals.editPost.privacyPrivateDesc'),
      icon: Lock 
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('modals.editPost.title')}</DialogTitle>
        </DialogHeader>

        {/* Media Manager */}
        {media.length > 0 && (
          <div className="space-y-2">
            <Label>{t('modals.editPost.mediaTitle', 'Photos & videos')} ({media.length})</Label>
            <p className="text-xs text-muted-foreground">
              {t('modals.editPost.mediaHint', 'Tap the trash icon to remove an item from this post.')}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {media.map((m: any) => {
                const isVideo = m.media_type?.includes('video');
                return (
                  <div key={m.id} className="relative group aspect-square rounded-md overflow-hidden bg-muted">
                    {isVideo ? (
                      <>
                        <video src={m.url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                        <PlayCircle className="absolute inset-0 m-auto h-8 w-8 text-white/90 drop-shadow pointer-events-none" />
                      </>
                    ) : (
                      <img src={m.url} alt="media" className="w-full h-full object-cover" />
                    )}
                    <button
                      type="button"
                      aria-label={t('common.delete')}
                      onClick={() => setMediaToDelete({ id: m.id, url: m.url })}
                      disabled={isDeletingMedia || media.length <= 1}
                      title={media.length <= 1 ? t('modals.editPost.cannotDeleteLast', 'A post must have at least one item') : t('common.delete')}
                      className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-destructive disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {isDeletingMedia ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-6 py-4">
          {/* Caption */}
          <div className="space-y-2">
            <Label htmlFor="caption">{t('modals.editPost.caption')}</Label>
            <Textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t('modals.editPost.captionPlaceholder')}
              className="resize-none"
              rows={4}
            />
          </div>

          {/* Privacy Setting */}
          <div className="space-y-3">
            <Label>{t('modals.editPost.privacy')}</Label>
            <RadioGroup value={privacy} onValueChange={(value) => setPrivacy(value as typeof privacy)}>
              {privacyOptions.map((option) => (
                <div 
                  key={option.value} 
                  className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => setPrivacy(option.value as typeof privacy)}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <option.icon className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor={option.value} className="cursor-pointer font-medium">
                        {option.label}
                      </Label>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isUpdating}>
            {isUpdating ? t('common.processing') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Confirm media deletion */}
      <AlertDialog open={!!mediaToDelete} onOpenChange={(o) => !o && setMediaToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('modals.editPost.deleteMediaTitle', 'Delete this item?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('modals.editPost.deleteMediaDesc', 'This will permanently remove the photo or video from the post. This cannot be undone.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteMedia}
              disabled={isDeletingMedia}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingMedia ? t('common.processing') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default EditPostModal;
